import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChatMessageUI } from '../../shared/chat/chat-message/chat-message';
import { LivekitService } from '../../core/services/livekit.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { MibSpinnerComponent } from '@medinbox/angular';
import { MedinboxHeader } from '../../shared/mib-header/medinbox-header';

@Component({
  selector: 'app-cleanfeed',
  imports: [ChatMessageUI, LottieComponent, MibSpinnerComponent, MedinboxHeader],
  templateUrl: './cleanfeed.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Cleanfeed implements AfterViewInit {
  private readonly livekit = inject(LivekitService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly audioEl = viewChild.required<ElementRef<HTMLAudioElement>>('audioEl');

  readonly isDisconnected = this.livekit.isDisconnected;
  readonly playingAudio = this.livekit.agentSpeaking.asReadonly();
  readonly agentAnswer = computed(
    () => this.livekit.agentMessages()[this.livekit.agentMessages().length - 1]?.message,
  );

  readonly waitingMessage = computed<string | null>(() => {
    if (!this.livekit.ingestReady()) return 'Waiting for the session to start…';
    if (!this.livekit.panelistReady()) return 'Waiting for a panelist to join…';

    return null;
  });

  readonly voiceAnimation: AnimationOptions = {
    path: './lottie/Recording.json',
    loop: true,
    autoplay: true,
  };

  ngAfterViewInit(): void {
    this.livekit.connect({ role: 'cleanfeed', participantName: 'cleanfeed' }).subscribe();

    toObservable(this.livekit.agentAudioTrack, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((track) => {
        const el = this.audioEl().nativeElement;
        if (track) {
          track.attach(el);
          el.play().catch(() => {});
        } else {
          el.srcObject = null;
        }
      });
  }
}
